import * as React from 'react';
import useI18n from '../../hooks/useI18n';
import type { ChipInputProps } from './types';

interface Chip {
  id: number;
  value: string;
}

interface ChipState {
  chips: Chip[];
  nextId: number;
}

type Options = Pick<
  ChipInputProps,
  | 'value'
  | 'defaultValue'
  | 'onValueChange'
  | 'deduplicate'
  | 'separatorPattern'
  | 'validateEdit'
>;

function reconcileValues(values: string[], previous: ChipState): ChipState {
  const available = new Map<string, Chip[]>();
  for (const chip of previous.chips) {
    const matches = available.get(chip.value) ?? [];
    matches.push(chip);
    available.set(chip.value, matches);
  }
  for (const matches of available.values()) matches.reverse();

  let nextId = previous.nextId;
  // External string arrays can only identify equal values by occurrence order.
  const chips = values.map(
    (value) => available.get(value)?.pop() ?? { id: nextId++, value },
  );
  return { chips, nextId };
}

export function useChipInputState({
  value,
  defaultValue = [],
  onValueChange,
  deduplicate = true,
  separatorPattern = /[,\n]+/,
  validateEdit,
}: Options) {
  const { t } = useI18n();
  const [state, setState] = React.useState<ChipState>(() =>
    reconcileValues(value ?? defaultValue, { chips: [], nextId: 0 }),
  );
  const [inputValue, setInputValue] = React.useState('');
  const [announcement, setAnnouncement] = React.useState('');

  if (
    value &&
    (value.length !== state.chips.length ||
      value.some((item, index) => item !== state.chips[index].value))
  ) {
    setState(reconcileValues(value, state));
  }

  const commit = (nextState: ChipState) => {
    if (onValueChange?.(nextState.chips.map((chip) => chip.value)) === false) {
      return false;
    }
    setState(nextState);
    return true;
  };

  const addChips = (rawValue: string) => {
    const values = rawValue
      .split(separatorPattern)
      .map((item) => item.trim())
      .filter(Boolean);
    if (!values.length) {
      setInputValue('');
      return;
    }
    const existingValues = new Set(state.chips.map((chip) => chip.value));
    let nextId = state.nextId;
    const additions: Chip[] = [];
    for (const item of values) {
      if (deduplicate && existingValues.has(item)) continue;
      additions.push({ id: nextId++, value: item });
      existingValues.add(item);
    }
    const accepted = commit({ chips: [...state.chips, ...additions], nextId });
    // Preserve the separator too when the consumer rejects typed input.
    setInputValue(accepted ? '' : rawValue);
  };

  const editChip = (id: number, nextValue: string) => {
    const duplicate =
      deduplicate &&
      state.chips.some((chip) => chip.id !== id && chip.value === nextValue);
    if (!nextValue || duplicate) return t('invalidValue');
    const error = validateEdit?.(nextValue);
    if (error) return error;
    const chips = state.chips.map((chip) =>
      chip.id === id ? { ...chip, value: nextValue } : chip,
    );
    if (!commit({ ...state, chips })) return t('invalidValue');
    setAnnouncement(t('chipSaved', { value: nextValue }));
    return null;
  };

  const announceRemoved = (removed: Chip[]) => {
    if (removed.length) {
      setAnnouncement(
        removed.map((chip) => t('delete', { value: chip.value })).join(', '),
      );
    }
  };

  const removeChip = (id: number) => {
    const chips = state.chips.filter((chip) => chip.id !== id);
    if (!commit({ ...state, chips })) return false;
    announceRemoved(state.chips.filter((chip) => chip.id === id));
    return true;
  };

  const replaceValues = (values: string[]) => {
    const nextState = reconcileValues(values, state);
    if (!commit(nextState)) return false;
    const remaining = new Set(nextState.chips.map((chip) => chip.id));
    announceRemoved(state.chips.filter((chip) => !remaining.has(chip.id)));
    return true;
  };

  return {
    chips: state.chips,
    values: state.chips.map((chip) => chip.value),
    inputValue,
    setInputValue,
    announcement,
    addChips,
    editChip,
    removeChip,
    replaceValues,
  };
}
